import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchRenewal,
  fetchRenewalConversation,
  sendRenewalReply,
  RenewalThread,
  RenewalMessage,
  RenewalApiEntry,
} from "./DataServices/RenewalDataService";
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
  EnvelopeIcon,
  ChevronDownIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import Loading from "./Loading";

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export default function RenewalEmailInbox() {
  const { renewalId } = useParams<{ renewalId: string }>();
  const navigate = useNavigate();

  const [renewal, setRenewal] = useState<RenewalApiEntry | null>(null);
  const [threads, setThreads] = useState<RenewalThread[]>([]);
  const [selectedThread, setSelectedThread] = useState<RenewalThread | null>(null);
  const [expandedMessages, setExpandedMessages] = useState<Record<string, boolean>>({});

  const [replyBody, setReplyBody] = useState("");
  const [replySubject, setReplySubject] = useState("");
  const [selectedRecipient, setSelectedRecipient] = useState("");

  const [loading, setLoading] = useState(true);
  const [loadingStep, setLoadingStep] = useState("Loading lease details...");
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);



  // Fetch renewal record and conversion threads
  const loadData = useCallback(async (isRefresh = false) => {
    if (!renewalId) return;
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
      setLoadingStep("Loading lease details...");
    }
    setError(null);

    try {
      const renewalData = await fetchRenewal(renewalId);
      setRenewal(renewalData);
      if (!isRefresh) {
        await delay(300);
        setLoadingStep("Connecting to Gmail...");
      }

      if (!isRefresh) {
        await delay(300);
        setLoadingStep(`Searching inbox for ${renewalData.tenant || "tenant"}...`);
      }

      const convThreads = await fetchRenewalConversation(renewalId);

      if (!isRefresh) {
        await delay(300);
        setLoadingStep("Structuring conversation history...");
        await delay(200);
      }

      setThreads(convThreads);

      // Default selected thread to the first one if none selected or if refreshing
      if (convThreads.length > 0) {
        setSelectedThread((prev) => {
          if (prev) {
            const updated = convThreads.find((t) => t.threadId === prev.threadId);
            return updated || convThreads[0];
          }
          return convThreads[0];
        });
      } else {
        setSelectedThread(null);
      }

      // Initialize recipient to the primary tenant email
      if (renewalData.occupants && renewalData.occupants.length > 0) {
        const primary = renewalData.occupants.find((o) => !o.isCosigner) || renewalData.occupants[0];
        setSelectedRecipient(primary.email);
      } else if (renewalData.email) {
        setSelectedRecipient(renewalData.email);
      }
    } catch (err: any) {
      console.error("Failed to load renewal inbox:", err);
      setError(err?.message || "An error occurred while loading conversation threads.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [renewalId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Scroll to top of messages and expand the first message when selected thread changes
  useEffect(() => {
    if (selectedThread) {
      const newExpanded: Record<string, boolean> = {};
      selectedThread.messages.forEach((msg, idx) => {
        // Expand the first message automatically
        newExpanded[msg.id] = idx === 0;
      });
      setExpandedMessages(newExpanded);

      // Pre-populate reply subject
      const baseSubject = selectedThread.subject || "";
      if (baseSubject.toLowerCase().startsWith("re:")) {
        setReplySubject(baseSubject);
      } else {
        setReplySubject(`Re: ${baseSubject}`);
      }

      // Scroll to top
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = 0;
        }
      }, 100);
    }
  }, [selectedThread]);

  const handleToggleMessage = (msgId: string) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [msgId]: !prev[msgId],
    }));
  };

  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!renewalId || !selectedThread || !selectedRecipient || !replyBody.trim()) return;

    // Last message in the thread to reply to
    const lastMsg = selectedThread.messages[selectedThread.messages.length - 1];

    setSending(true);
    try {
      await sendRenewalReply(renewalId, {
        toEmail: selectedRecipient,
        subject: replySubject || `Re: ${selectedThread.subject}`,
        body: replyBody,
        threadId: selectedThread.threadId,
        replyToMessageId: lastMsg?.messageId || undefined,
      });

      setReplyBody("");
      // Refresh conversation thread
      await loadData(true);
    } catch (err: any) {
      console.error("Failed to send reply:", err);
      alert(err?.message || "Failed to send email reply. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getCleanSenderName = (from: string) => {
    if (!from) return "Unknown Sender";
    const match = from.match(/^"?(.*?)"?\s*<.*?>$/);
    if (match) return match[1];
    return from;
  };

  const getCleanSenderEmail = (from: string) => {
    if (!from) return "";
    const match = from.match(/<([^>]+)>/);
    if (match) return match[1];
    return from;
  };

  const cleanSnippet = (text: string) => {
    if (!text) return "";
    let clean = text.replace(/<\/?[^>]+(>|$)/g, "");
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = clean;
    return tempDiv.textContent || tempDiv.innerText || clean;
  };

  if (loading) {
    const progressPercent = (() => {
      if (loadingStep.startsWith("Loading lease")) return 25;
      if (loadingStep.startsWith("Connecting")) return 50;
      if (loadingStep.startsWith("Searching")) return 75;
      if (loadingStep.startsWith("Structuring")) return 95;
      return 100;
    })();

    return (
      <div className="h-screen flex items-center justify-center bg-slate-50 font-sans">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 shadow-xl text-center">
          <div className="relative flex items-center justify-center w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full bg-blue-100 animate-ping opacity-75"></div>
            <div className="absolute inset-2 rounded-full bg-blue-200/50 animate-pulse"></div>
            <div className="absolute inset-0 rounded-full border-2 border-dashed border-blue-400 animate-spin" style={{ animationDuration: "8s" }}></div>
            <div className="relative w-14 h-14 rounded-full bg-blue-600 flex items-center justify-center shadow-md shadow-blue-200">
              <EnvelopeIcon className="w-7 h-7 text-white animate-bounce" style={{ animationDuration: "2.5s" }} />
            </div>
          </div>

          <h3 className="text-md font-extrabold text-slate-800 mb-1">Searching Gmail Conversations</h3>
          <p className="text-xs text-slate-500 min-h-[32px] flex items-center justify-center px-4 leading-relaxed font-semibold">
            {loadingStep}
          </p>

          <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden my-4 shadow-inner">
            <div 
              className="bg-gradient-to-r from-blue-500 to-indigo-600 h-full rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>

          <span className="text-[10px] font-extrabold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
            {progressPercent}% Complete
          </span>

          {renewal && (
            <div className="mt-6 pt-4 border-t border-slate-100 text-left">
              <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block mb-2">Target Lease Details</span>
              <div className="bg-slate-50 border border-slate-200/50 rounded-xl p-3.5 space-y-1.5 shadow-2xs">
                <div className="flex justify-between text-[11px] font-bold text-slate-500">
                  <span>Unit:</span>
                  <span className="text-slate-800 font-semibold">{renewal.unit}</span>
                </div>
                <div className="flex justify-between text-[11px] font-bold text-slate-500">
                  <span>Building:</span>
                  <span className="text-slate-800 font-semibold truncate max-w-[220px]">{renewal.building}</span>
                </div>
                <div className="flex justify-between text-[11px] font-bold text-slate-500">
                  <span>Tenant:</span>
                  <span className="text-slate-800 font-semibold truncate max-w-[220px]">{renewal.tenant}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const occupantsList = renewal?.occupants || [];

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col bg-slate-50 font-sans">
      {/* Top Banner Navigation */}
      <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-slate-200">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/renewal")}
            className="flex items-center justify-center p-2 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-all duration-200"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <EnvelopeIcon className="w-5 h-5 text-blue-500" />
              Renewal Inbox
            </h1>
            {renewal && (
              <p className="text-xs text-slate-500 font-semibold mt-0.5">
                Unit {renewal.unit} &middot; {renewal.building} &middot; Tenants: {renewal.tenant}
              </p>
            )}
          </div>
        </div>

        <button
          onClick={() => loadData(true)}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 text-slate-600 hover:text-slate-800 bg-white hover:bg-slate-50 text-xs font-semibold rounded-lg shadow-sm transition-all disabled:opacity-50"
        >
          <ArrowPathIcon className={`w-4 h-4 ${refreshing ? "animate-spin text-blue-500" : ""}`} />
          {refreshing ? "Syncing..." : "Sync Gmail"}
        </button>
      </div>

      {error ? (
        <div className="flex-1 flex flex-col items-center justify-center p-6 bg-slate-50">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md text-center shadow-sm">
            <span className="text-3xl">⚠️</span>
            <h3 className="text-sm font-bold text-red-800 mt-2">Failed to load conversation</h3>
            <p className="text-xs text-red-600 mt-1">{error}</p>
            <button
              onClick={() => loadData()}
              className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold text-xs rounded-lg transition-all"
            >
              Try Again
            </button>
          </div>
        </div>
      ) : threads.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center p-6 bg-slate-50 text-slate-400">
          <div className="text-center bg-white border border-slate-200 rounded-xl p-12 max-w-lg shadow-sm">
            <EnvelopeIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-md font-bold text-slate-700 mb-1">No lease renewal conversations found</h3>
            <p className="text-xs text-slate-500 leading-relaxed">
              We couldn't find any Gmail threads matching the emails of the occupants of this unit. 
              {occupantsList.length > 0 ? (
                <span> Checked: {occupantsList.map((o) => o.email).join(", ")}</span>
              ) : (
                <span className="text-red-500 font-semibold"> (Warning: No tenant emails mapped to this unit!)</span>
              )}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel: Thread List (Gmail style) */}
          <div className="w-80 border-r border-slate-200 bg-white flex flex-col flex-shrink-0 overflow-y-auto">
            <div className="p-3 border-b border-slate-100 bg-slate-50/50">
              <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">
                Conversations ({threads.length})
              </span>
            </div>
            <div className="divide-y divide-slate-100">
              {threads.map((thread) => {
                const isSelected = selectedThread?.threadId === thread.threadId;
                const lastMsg = thread.messages[thread.messages.length - 1];
                const fromName = getCleanSenderName(lastMsg?.from || "");

                return (
                  <div
                    key={thread.threadId}
                    onClick={() => setSelectedThread(thread)}
                    className={`p-4 cursor-pointer transition duration-150 ${
                      isSelected
                        ? "bg-blue-50/50 border-l-4 border-blue-500"
                        : "hover:bg-slate-50 border-l-4 border-transparent"
                    }`}
                  >
                    <div className="flex justify-between items-start gap-2 mb-1">
                      <span className="font-bold text-xs text-slate-800 truncate max-w-[170px]">
                        {fromName}
                      </span>
                      <span className="text-[9px] font-semibold text-slate-400 whitespace-nowrap">
                        {formatDate(thread.lastMessageDate)}
                      </span>
                    </div>
                    <h4 className="text-xs font-semibold text-slate-700 truncate mb-1">
                      {thread.subject}
                    </h4>
                    <p className="text-[10px] text-slate-400 line-clamp-2 leading-relaxed">
                      {cleanSnippet(thread.snippet || lastMsg?.snippet || "")}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Panel: Thread Conversation View */}
          <div className="flex-1 flex flex-col bg-slate-50 overflow-hidden">
            {selectedThread ? (
              <>
                {/* Subject Header */}
                <div className="bg-white border-b border-slate-200 px-6 py-4 shadow-2xs flex justify-between items-center">
                  <h2 className="text-sm font-extrabold text-slate-800 truncate">
                    {selectedThread.subject}
                  </h2>
                  <span className="text-[10px] font-bold text-slate-400 bg-slate-100 border border-slate-200/50 px-2 py-0.5 rounded-full">
                    {selectedThread.messages.length} messages
                  </span>
                </div>

                {/* Conversation Body (Scrollable) */}
                <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
                  {selectedThread.messages.map((msg, idx) => {
                    const isExpanded = expandedMessages[msg.id];
                    const fromName = getCleanSenderName(msg.from);
                    const fromEmail = getCleanSenderEmail(msg.from);
                    const isSystemSender = fromEmail.toLowerCase() === renewal?.email.toLowerCase() || 
                                          fromEmail.toLowerCase().includes("thedowntownapts.com") || 
                                          fromEmail.toLowerCase().includes("management");

                    return (
                      <div
                        key={msg.id}
                        className={`bg-white rounded-xl border transition-all duration-200 overflow-hidden shadow-2xs ${
                          isExpanded ? "border-slate-200" : "border-slate-200/60 hover:border-slate-300"
                        }`}
                      >
                        {/* Message Header (clickable to collapse/expand) */}
                        <div
                          onClick={() => handleToggleMessage(msg.id)}
                          className="px-5 py-3 flex items-center justify-between cursor-pointer bg-white hover:bg-slate-50/50 select-none"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${
                              isSystemSender ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"
                            }`}>
                              {fromName.charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <span className="text-xs font-bold text-slate-800 block truncate">
                                {fromName}
                                <span className="text-[10px] font-normal text-slate-500 ml-1.5 hidden sm:inline">
                                  &lt;{fromEmail}&gt;
                                </span>
                              </span>
                              {!isExpanded && (
                                <p className="text-[10px] text-slate-400 truncate mt-0.5">
                                  {cleanSnippet(msg.snippet || "")}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2.5">
                            <span className="text-[10px] font-semibold text-slate-400">
                              {formatDate(msg.date)}
                            </span>
                            <ChevronDownIcon
                              className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${
                                isExpanded ? "rotate-180" : ""
                              }`}
                            />
                          </div>
                        </div>

                        {/* Expanded Message Content */}
                        {isExpanded && (
                          <div className="px-5 pb-5 pt-3 border-t border-slate-100 bg-white">
                            <div className="text-[10px] text-slate-400 mb-4 flex flex-col gap-0.5 pb-3 border-b border-slate-100/50">
                              <span><span className="font-bold">From:</span> {msg.from}</span>
                              <span><span className="font-bold">To:</span> {msg.to}</span>
                              {msg.date && <span><span className="font-bold">Date:</span> {new Date(msg.date).toUTCString()}</span>}
                            </div>
                            {/* Render decrypted HTML or Plaintext email safely */}
                            {msg.body ? (
                              <div
                                className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed email-body overflow-x-auto max-w-full font-normal"
                                dangerouslySetInnerHTML={{
                                  __html: msg.body.includes("<br") || msg.body.includes("<div") || msg.body.includes("<p")
                                    ? msg.body
                                    : msg.body.replace(/\n/g, "<br />")
                                }}
                              />
                            ) : (
                              <p className="text-xs text-slate-400 italic">No message body content</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply Form */}
                <div className="bg-white border-t border-slate-200 px-6 py-4 shadow-lg">
                  <form onSubmit={handleSendReply} className="space-y-3">
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-bold text-slate-500">To:</span>
                        <div className="relative">
                          <select
                            value={selectedRecipient}
                            onChange={(e) => setSelectedRecipient(e.target.value)}
                            className="bg-slate-100 hover:bg-slate-200/80 border border-slate-200 text-slate-800 text-xs font-bold rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-100 transition-all cursor-pointer appearance-none pr-8"
                          >
                            {occupantsList.map((occ) => (
                              <option key={occ.id} value={occ.email}>
                                {occ.name} ({occ.email}) {occ.isCosigner ? "[Guarantor]" : ""}
                              </option>
                            ))}
                            {/* Fallback to primary unit email if not already in list */}
                            {renewal?.email && !occupantsList.some((o) => o.email === renewal.email) && (
                              <option value={renewal.email}>
                                {renewal.tenant} ({renewal.email})
                              </option>
                            )}
                          </select>
                          <ChevronDownIcon className="w-3.5 h-3.5 text-slate-500 absolute right-2.5 top-2.5 pointer-events-none" />
                        </div>
                      </div>

                      <div className="flex-1 min-w-[200px] flex items-center gap-1.5">
                        <span className="text-xs font-bold text-slate-500">Subject:</span>
                        <input
                          type="text"
                          value={replySubject}
                          onChange={(e) => setReplySubject(e.target.value)}
                          className="flex-1 bg-slate-50 hover:bg-slate-100/50 border border-slate-200 text-slate-800 text-xs font-semibold rounded-lg px-3 py-1.5 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 transition-all"
                          placeholder="Re: Subject"
                          required
                        />
                      </div>
                    </div>

                    <div className="relative">
                      <textarea
                        value={replyBody}
                        onChange={(e) => setReplyBody(e.target.value)}
                        disabled={sending}
                        rows={5}
                        className="w-full bg-slate-50 hover:bg-slate-100/50 border border-slate-200 text-xs text-slate-800 placeholder-slate-400 rounded-xl p-4 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 resize-none transition-all disabled:opacity-60"
                        placeholder={`Reply to ${selectedRecipient}...`}
                        required
                      />
                      {sending && (
                        <div className="absolute inset-0 bg-white/70 rounded-xl flex items-center justify-center">
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-xs font-bold text-slate-600">Sending Email...</span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={sending || !replyBody.trim()}
                        className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-white text-xs font-bold rounded-xl shadow-sm transition-all"
                      >
                        <PaperAirplaneIcon className="w-4 h-4" />
                        Send Reply
                      </button>
                    </div>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-6 bg-slate-50 text-slate-400">
                <EnvelopeIcon className="w-12 h-12 text-slate-300 mb-2" />
                <p className="text-xs">Select a conversation thread to view messages</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
